import {
  collection,
  deleteDoc,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  setDoc,
  startAfter,
} from "firebase/firestore";
import { firestoreDb } from "../App";
import { getFormattedDate } from "../utils/commons";
import http from "../utils/http";
import { sendPutRequest } from "./ContentServices";

export interface TransformationWOD {
  date: string;
  /** S3 object key; resolve with `resolveWodImage` against `app_config/assets`. */
  imageKey?: string;
  createdAt?: number;
}

const TRANSFORMATION_WOD_COLLECTION = "transformation_wods";
const TRANSFORMATION_WOD_PAGE_SIZE = 20;

const getImageUploadEndpointPath = (): string => {
  return (
    process.env.REACT_APP_TRANSFORMATION_WOD_UPLOAD_PATH ||
    "/admin/transformation-wods/upload-image"
  );
};

export const getTodaysTransformationWod =
  async (): Promise<TransformationWOD | null> => {
    const formattedDate = getFormattedDate();
    const docRef = doc(
      firestoreDb,
      TRANSFORMATION_WOD_COLLECTION,
      formattedDate
    );
    const docSnap = await getDoc(docRef);
    if (!docSnap.data()) {
      return null;
    }

    return docSnap.data() as TransformationWOD;
  };

export const fetchPastTransformationWods = async (
  lastDoc?: QueryDocumentSnapshot<DocumentData>
): Promise<{
  wods: TransformationWOD[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  hasMore: boolean;
}> => {
  let q = query(
    collection(firestoreDb, TRANSFORMATION_WOD_COLLECTION),
    orderBy("createdAt", "desc"),
    limit(TRANSFORMATION_WOD_PAGE_SIZE)
  );

  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }

  const snap = await getDocs(q);
  const lastVisible = snap.docs[snap.docs.length - 1];

  return {
    wods: snap.docs.map((d) => d.data() as TransformationWOD),
    lastDoc: lastVisible,
    hasMore: snap.docs.length === TRANSFORMATION_WOD_PAGE_SIZE,
  };
};

export const saveTransformationWod = async (
  date: string,
  imageKey: string
): Promise<boolean> => {
  const docRef = doc(firestoreDb, TRANSFORMATION_WOD_COLLECTION, date);
  const existingDoc = await getDoc(docRef);
  const existingCreatedAt = existingDoc.data()?.createdAt as number | undefined;

  // setDoc replaces the whole doc, so any legacy `imageUrl` is dropped too.
  await setDoc(docRef, {
    date,
    imageKey,
    createdAt: existingCreatedAt || Date.now(),
  });

  return true;
};

export const deleteTransformationWod = async (date?: string): Promise<void> => {
  let dateToDelete = getFormattedDate();
  if (date) {
    dateToDelete = date;
  }

  await deleteDoc(
    doc(firestoreDb, TRANSFORMATION_WOD_COLLECTION, dateToDelete)
  );
};

export const cloneTransformationWorkout = async (
  wod: TransformationWOD
): Promise<boolean> => {
  if (!wod.imageKey) {
    throw new Error("Transformation WOD has no image");
  }

  const formattedDate = getFormattedDate();

  await setDoc(doc(firestoreDb, TRANSFORMATION_WOD_COLLECTION, formattedDate), {
    date: formattedDate,
    imageKey: wod.imageKey,
    createdAt: Date.now(),
  });

  return true;
};

export const getTransformationWodSignedUploadUrl = async (
  fileName: string
): Promise<string> => {
  const uploadPath = getImageUploadEndpointPath();
  const result = await http.get({ path: `${uploadPath}?fileName=${fileName}` });
  const signedUrl = result.data?.data as string | undefined;

  if (!signedUrl) {
    throw new Error("Failed to fetch signed upload url");
  }

  return signedUrl;
};

/**
 * The presigned URL is virtual-hosted on the bucket, so its path is the
 * object key. Kept percent-encoded, matching how the backend stores keys.
 */
export const getObjectKeyFromSignedUrl = (signedUrl: string): string =>
  new URL(signedUrl).pathname.replace(/^\/+/, "");

export const uploadTransformationWodImage = async (
  file: File
): Promise<string> => {
  const signedUrl = await getTransformationWodSignedUploadUrl(file.name);
  const uploadResult = await sendPutRequest({ signUrl: signedUrl, file });

  if (uploadResult.status !== 200) {
    throw new Error("Image upload failed");
  }

  return getObjectKeyFromSignedUrl(signedUrl);
};
